// ============================================================
//  src/storage/managed-drive-provider.js
//  Provider para el Drive administrado (managed_drive)
//  Usa Cloud Functions — sin credenciales en cliente
// ============================================================

import { auth, functions }  from '../firebase.js';
import { httpsCallable }    from 'firebase/functions';
import { getRuntimeFlags }  from '../runtime-flags.js';

// ── Helper ────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Contrato del provider ─────────────────────────────────────

/**
 * Contrato:
 *   isAuthorized()          → boolean
 *   requestAuthorization()  → Promise<boolean>
 *   upload(file, context)   → Promise<{ fileId, fileUrl, providerMeta }>
 *   delete(fileId)          → Promise<boolean>
 *
 * context esperado en upload: { edicion, tipo }
 *   edicion.id — ID de la edición en Firestore
 *   tipo       — 'log' | 'conf' | 'res' | 'cert' | 'reg'
 *
 * Habilitación: el admin configura managedDriveUsers/{uid}.enabled en Firestore
 */
const managedDriveProvider = {

  isAuthorized() {
    return !!auth.currentUser;
  },

  async requestAuthorization() {
    return !!auth.currentUser;
  },

  async upload(file, context) {
    if (!auth.currentUser) throw new Error('Usuario no autenticado.');

    const _flags = await getRuntimeFlags();
    if (_flags.emergencyMode || !_flags.managedDriveEnabled) {
      throw new Error('Almacenamiento CM temporalmente deshabilitado.');
    }

    const fileDataBase64 = await fileToBase64(file);

    const fn = httpsCallable(functions, 'managedDriveUpload');
    const { data } = await fn({
      fileDataBase64,
      mimeType:  file.type || 'application/octet-stream',
      fileName:  file.name,
      fileSize:  file.size,
      edicionId: context.edicion.id,
      tipo:      context.tipo,
    });

    if (!data?.ok) {
      throw new Error(data?.error || 'Error en managed_drive upload.');
    }

    return {
      fileId:      data.fileId,
      fileUrl:     data.fileUrl,
      providerMeta: null,
    };
  },

  async delete(fileId) {
    if (!auth.currentUser) throw new Error('Usuario no autenticado.');

    const _flags = await getRuntimeFlags();
    if (_flags.emergencyMode || !_flags.managedDriveEnabled) {
      throw new Error('Almacenamiento CM temporalmente deshabilitado.');
    }

    const fn = httpsCallable(functions, 'managedDriveDelete');
    const { data } = await fn({ fileId });

    if (!data?.ok) {
      throw new Error(data?.error || 'Error en managed_drive delete.');
    }

    return true;
  },

};

export default managedDriveProvider;
